import { useState, useEffect, useCallback } from "react";
import SuperAdminService from "../services/superAdminService";
import { Helmet } from "react-helmet";

const SuperAdminDashboard = () => {
    const [users, setUsers] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [expandedId, setExpandedId] = useState(null);

    const fetchUsers = useCallback((page, searchValue) => {
        setLoading(true);
        setError("");
        SuperAdminService.getAllUsers(page, 20, searchValue)
            .then((res) => {
                setUsers(res.data.users);
                setPagination(res.data.pagination);
            })
            .catch((err) => {
                setError(err.response?.data?.message || "Error loading users");
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchUsers(1, "");
    }, [fetchUsers]);

    useEffect(() => {
        const handle = setTimeout(() => {
            fetchUsers(1, search);
        }, 400);
        return () => clearTimeout(handle);
    }, [search, fetchUsers]);

    const formatDate = (date) => {
        if (!date) return "-";
        return new Date(date).toLocaleDateString("hr-HR", {
            day: "2-digit", month: "2-digit", year: "numeric"
        });
    };

    const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : "-";

    const statusBadge = (user) => {
        if (!user.isActivated) return { label: "Neaktiviran", color: "bg-gray-200 text-gray-700" };
        if (user.subscription === "premium") return { label: "Premium", color: "bg-pink-200 text-pink-800" };
        if (user.paymentStatus === "paid") return { label: "Plaćeno", color: "bg-cyan-200 text-cyan-800" };
        return { label: "Free", color: "bg-white text-gray-600 border border-gray-300" };
    };

    const toggleExpand = (userId) => {
        setExpandedId((prev) => (prev === userId ? null : userId));
    };

    return (
        <>
            <Helmet>
                <title>Super Admin — Weddinfy</title>
            </Helmet>
            <div className="min-h-screen bg-gradient-to-br from-cyan-200 via-white to-pink-300 p-4 md:p-8">
                <div className="max-w-6xl mx-auto">
                    <div className="mb-6">
                        <h1 className="text-3xl md:text-4xl font-bold text-gray-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                            Super Admin
                        </h1>
                        <p className="text-gray-600 mt-1">Ukupno korisnika: {pagination.total}</p>
                    </div>

                    <div className="mb-4">
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Pretraži po emailu ili korisničkom imenu..."
                            className="w-full md:w-96 px-4 py-2 rounded-lg border border-gray-300 focus:border-cyan-400 focus:outline-none bg-white/80"
                        />
                    </div>

                    {error && (
                        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">{error}</div>
                    )}

                    <div className="bg-white/90 backdrop-blur rounded-xl shadow-md overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gradient-to-r from-cyan-100 to-pink-100 text-gray-700">
                                    <tr>
                                        <th className="text-left px-4 py-3 font-semibold">Email</th>
                                        <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Korisničko ime</th>
                                        <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Registriran</th>
                                        <th className="text-left px-4 py-3 font-semibold">Status</th>
                                        <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Jezik</th>
                                        <th className="px-4 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading && (
                                        <tr><td colSpan="6" className="text-center py-8 text-gray-500">Učitavanje...</td></tr>
                                    )}
                                    {!loading && users.length === 0 && (
                                        <tr><td colSpan="6" className="text-center py-8 text-gray-500">Nema korisnika.</td></tr>
                                    )}
                                    {!loading && users.map((u) => {
                                        const badge = statusBadge(u);
                                        const isExpanded = expandedId === u._id;
                                        return (
                                            <UserRow
                                                key={u._id}
                                                user={u}
                                                badge={badge}
                                                isExpanded={isExpanded}
                                                onToggle={() => toggleExpand(u._id)}
                                                formatDate={formatDate}
                                                capitalize={capitalize}
                                            />
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {pagination.totalPages > 1 && (
                        <div className="flex justify-between items-center mt-4 text-sm">
                            <button
                                disabled={pagination.page <= 1}
                                onClick={() => { setExpandedId(null); fetchUsers(pagination.page - 1, search); }}
                                className="px-4 py-2 bg-white/80 rounded-lg border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white"
                            >
                                ← Prethodna
                            </button>
                            <span className="text-gray-700">
                                Stranica {pagination.page} / {pagination.totalPages}
                            </span>
                            <button
                                disabled={pagination.page >= pagination.totalPages}
                                onClick={() => { setExpandedId(null); fetchUsers(pagination.page + 1, search); }}
                                className="px-4 py-2 bg-white/80 rounded-lg border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white"
                            >
                                Sljedeća →
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

const UserRow = ({ user, badge, isExpanded, onToggle, formatDate, capitalize }) => {
    const { wedding, stats } = user;

    return (
        <>
            <tr
                onClick={onToggle}
                className="border-t border-gray-100 hover:bg-cyan-50/50 cursor-pointer transition"
            >
                <td className="px-4 py-3 text-gray-800">{user.email}</td>
                <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{user.userName || "-"}</td>
                <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">{formatDate(user.createdAt)}</td>
                <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                        {badge.label}
                    </span>
                </td>
                <td className="px-4 py-3 text-gray-600 hidden md:table-cell uppercase">{user.language}</td>
                <td className="px-4 py-3 text-gray-400">
                    <span className={`inline-block transition-transform ${isExpanded ? "rotate-180" : ""}`}>▾</span>
                </td>
            </tr>
            {isExpanded && (
                <tr className="border-t border-gray-100 bg-gradient-to-br from-cyan-50/50 to-pink-50/50">
                    <td colSpan="6" className="px-4 py-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-semibold">Korisnik</h3>
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <InfoItem label="Telefon" value={user.phone || "-"} />
                                    <InfoItem label="Aktiviran" value={user.isActivated ? "Da" : "Ne"} />
                                    <InfoItem label="Subscription" value={capitalize(user.subscription)} />
                                    <InfoItem label="Payment" value={capitalize(user.paymentStatus)} />
                                    {user.premiumExpiryDate && (
                                        <InfoItem label="Premium istek" value={formatDate(user.premiumExpiryDate)} />
                                    )}
                                </div>
                            </div>

                            <div>
                                <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-semibold">Svadba</h3>
                                {wedding ? (
                                    <>
                                        <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                                            <InfoItem label="Par" value={`${capitalize(wedding.brideName)} & ${capitalize(wedding.groomName)}`} />
                                            <InfoItem label="Datum" value={formatDate(wedding.date)} />
                                            <InfoItem label="Grad" value={capitalize(wedding.city)} />
                                            <InfoItem label="Lokacija" value={capitalize(wedding.location)} />
                                            <InfoItem
                                                label="Pozivnica"
                                                value={stats?.hasInvitation ? "Dodana ✓" : "Nije dodana"}
                                                valueClass={stats?.hasInvitation ? "text-green-700" : "text-gray-500"}
                                            />
                                        </div>
                                        <div className="grid grid-cols-3 gap-2">
                                            <StatCard label="Gosti" value={stats?.guestCount || 0} />
                                            <StatCard label="Potvrđenih" value={stats?.attendingCount || 0} />
                                            <StatCard label="Stolovi" value={stats?.tableCount || 0} />
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-sm text-gray-500 italic">Korisnik još nije kreirao svadbu.</div>
                                )}
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

const InfoItem = ({ label, value, valueClass = "text-gray-800" }) => (
    <div>
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-0.5">{label}</div>
        <div className={`font-medium ${valueClass}`}>{value}</div>
    </div>
);

const StatCard = ({ label, value }) => (
    <div className="bg-white/80 rounded-lg p-2 text-center shadow-sm">
        <div className="text-xl font-bold text-gray-800">{value}</div>
        <div className="text-xs uppercase tracking-wide text-gray-600">{label}</div>
    </div>
);

export default SuperAdminDashboard;
